/**
 * Version-chain lookup for cited files, keyed by filename.
 *
 * The cabinet catalog already knows which physical files are versions of one
 * logical document (`GET /api/v3/cabinet/graph` → document nodes carry
 * `meta.versions`). The evidence panel only needs one sentence out of that, so
 * this projects the graph down to a flat filename → note map once per session.
 *
 * Three properties this module exists to guarantee:
 *
 * 1. ONE fetch per session. The promise is memoised at module scope, so ten
 *    PdfFactCards mounting at once share a single request and there is no
 *    polling — the version chain of an already-cited file does not change
 *    underneath a rendered answer.
 * 2. NEVER a UI flag. The graph endpoint is live regardless of
 *    `REACT_APP_CABINET_UI_ENABLED`, which gates the /cabinet page, not the API.
 * 3. FAILURE IS SILENCE. A missing catalog, an unmatched filename, or an API
 *    error all resolve to an empty index, so the card renders exactly what it
 *    rendered before this line existed.
 */

import { cabinetService } from "../../services/cabinetService";
import type { CabinetGraph, CabinetVersion } from "../../types/cabinet";

/** The one quiet line a card shows, already resolved to text. */
export interface DocumentVersionNote {
	text: string;
	/** Drives the chip colour — reuses the cabinet status palette. */
	status: CabinetVersion["status"];
}

export type DocumentVersionIndex = ReadonlyMap<string, DocumentVersionNote>;

const EMPTY_INDEX: DocumentVersionIndex = new Map();

/** `landing/2026/Q2/Federal Hill OM.pdf` → `federal hill om.pdf`. */
function basenameKey(relpath: string): string {
	const tail = relpath.split("/").pop() ?? relpath;
	return tail.trim().toLowerCase();
}

function noteFor(
	version: CabinetVersion,
	family: CabinetVersion[],
): DocumentVersionNote | null {
	if (version.status === "current") {
		const earlier = family.length - 1;
		return {
			status: "current",
			text: `Current version · supersedes ${earlier} earlier ${
				earlier === 1 ? "file" : "files"
			}`,
		};
	}
	const current = family.find((v) => v.status === "current");
	// A family with no current member is a catalog state we have nothing
	// useful to say about — better silent than confidently wrong.
	if (!current) return null;
	const label = current.relpath.split("/").pop() ?? current.relpath;
	return {
		status: version.status,
		text:
			version.status === "duplicate"
				? `Duplicate of ${label}`
				: `Superseded by ${label}`,
	};
}

export function buildVersionIndex(
	graph: CabinetGraph | null,
): DocumentVersionIndex {
	if (!graph) return EMPTY_INDEX;
	const index = new Map<string, DocumentVersionNote>();
	for (const node of graph.nodes) {
		if (node.kind !== "document") continue;
		const family = node.meta.versions ?? [];
		// A single-member family is just a file. Nothing to say.
		if (family.length < 2) continue;
		for (const version of family) {
			const note = noteFor(version, family);
			if (!note) continue;
			const key = basenameKey(version.relpath);
			// First writer wins: if two deals hold files with the same basename
			// we cannot tell which one was cited, so we keep the reading stable
			// rather than letting iteration order decide it each render.
			if (!index.has(key)) index.set(key, note);
		}
	}
	return index;
}

let pending: Promise<DocumentVersionIndex> | null = null;

/** Load (once per session) the filename → version-note index. */
export function loadDocumentVersionIndex(): Promise<DocumentVersionIndex> {
	if (!pending) {
		pending = cabinetService
			.getCabinetGraph()
			.then(buildVersionIndex)
			.catch(() => EMPTY_INDEX);
	}
	return pending;
}

/** Test seam — drops the memoised promise. */
export function resetDocumentVersionIndex(): void {
	pending = null;
}

export function lookupVersionNote(
	index: DocumentVersionIndex,
	filename: string | null | undefined,
): DocumentVersionNote | null {
	if (!filename) return null;
	return index.get(basenameKey(filename)) ?? null;
}
